import { useEffect, useState, useCallback } from "react";
import { PackagePlus, ExternalLink, Copy, CheckCircle2, Loader2, AlertTriangle, Truck, Warehouse, Briefcase, ChevronDown, ChevronRight, Plus, Zap, Trash2, TrendingUp } from "lucide-react";
import {
    fetchShoppingBySupplier,
    markGroupOrdered,
    createShoppingItem,
    cancelShoppingItem,
    triggerAutoRestock,
    fetchShoppingTotals,
    fetchJobs,
    formatApiError,
} from "@/lib/api";
import { PageHeader } from "@/components/dashboard/Primitives";
import IndkoebTabs from "@/components/dashboard/IndkoebTabs";
import { SpecialNeedsSection } from "@/components/dashboard/SpecialOrderSections";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const UNITS = ["stk", "m", "m²", "kg", "pakke", "rulle", "liter"];

function fmtKr(n) {
    if (n == null || isNaN(n)) return "—";
    return Number(n).toLocaleString("da-DK", { style: "currency", currency: "DKK", maximumFractionDigits: 2 });
}

function formatCopyBlock(items) {
    const lines = items.map((it) => {
        const parts = [];
        parts.push(it.material_number || "—");
        parts.push(it.item_name || "");
        parts.push(String(it.quantity_requested));
        parts.push(it.unit || "");
        return parts.join(" | ");
    });
    return lines.join("\n");
}

export default function OrderMode() {
    const { user } = useAuth();
    const canBatch = user && ["owner", "admin", "kontor"].includes((user.role || "").toLowerCase());
    const canSeeTotals = user && ["owner", "admin", "kontor"].includes((user.role || "").toLowerCase());
    const isPrivileged = user && ["owner", "admin"].includes((user.role || "").toLowerCase());

    const [groups, setGroups] = useState([]);
    const [totals, setTotals] = useState(null);
    const [loading, setLoading] = useState(true);
    const [gated, setGated] = useState(false);
    const [selected, setSelected] = useState({});
    const [expanded, setExpanded] = useState({});
    const [orderDialog, setOrderDialog] = useState(null);
    const [addOpen, setAddOpen] = useState(false);
    const [cancelItem, setCancelItem] = useState(null);
    const [copyToast, setCopyToast] = useState({});

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchShoppingBySupplier();
            setGroups(data);
            setGated(false);
            const init = {};
            data.forEach((g) => { init[g.supplier] = true; });
            setExpanded(init);
            if (canSeeTotals) {
                try { setTotals(await fetchShoppingTotals()); } catch { /* ignore */ }
            }
        } catch (err) {
            if (err?.response?.status === 503) { setGated(true); setGroups([]); }
            else toast.error(formatApiError(err));
        } finally { setLoading(false); }
    }, [canSeeTotals]);

    useEffect(() => { refresh(); }, [refresh]);

    const toggleItem = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
    const toggleAll = (supplier, items, check) => {
        setSelected((s) => {
            const n = { ...s };
            items.forEach((it) => { n[it.id] = check; });
            return n;
        });
    };
    const groupSelected = (items) => items.filter((it) => selected[it.id]).map((it) => it.id);
    const isAllSelected = (items) => items.length > 0 && items.every((it) => selected[it.id]);

    const copyGroup = async (supplier, items) => {
        const chosenIds = groupSelected(items);
        const toCopy = chosenIds.length > 0 ? items.filter((i) => chosenIds.includes(i.id)) : items;
        const text = formatCopyBlock(toCopy);
        try {
            await navigator.clipboard.writeText(text);
            setCopyToast((c) => ({ ...c, [supplier]: true }));
            setTimeout(() => setCopyToast((c) => ({ ...c, [supplier]: false })), 2000);
            toast.success(`${toCopy.length} varer kopieret`);
        } catch { toast.error("Kunne ikke kopiere til udklipsholder"); }
    };

    if (gated) {
        return (
            <div className="p-5 sm:p-8 max-w-4xl mx-auto">
                <IndkoebTabs />
                <PageHeader title="Indkøbsliste" subtitle="Native indkøbsliste er ikke aktiveret — specialbestillinger fra AI-opkald vises nedenfor." />
                <SpecialNeedsSection />
                <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-6 text-amber-900">
                    <div className="flex items-center gap-2 font-semibold mb-2"><AlertTriangle className="w-5 h-5" /> Native indkøbsliste ikke aktiveret</div>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="order-mode-page" className="p-5 sm:p-8 max-w-7xl mx-auto">
            <IndkoebTabs />
            <PageHeader
                title="Indkøbsliste"
                subtitle="Åbne varer grupperet efter leverandør. Tilføj varer, kopiér varenumre, åbn leverandørens hjemmeside, og markér som bestilt."
                right={
                    <div className="flex flex-wrap items-center gap-2">
                        {isPrivileged && (
                            <Button
                                data-testid="shopping-auto-restock-btn"
                                variant="outline"
                                onClick={async () => {
                                    try {
                                        const r = await triggerAutoRestock();
                                        toast.success(r.created_count > 0 ? `${r.created_count} varer tilføjet fra lavt lager` : "Alt lager er OK — ingen nye varer");
                                        refresh();
                                    } catch (err) { toast.error(formatApiError(err)); }
                                }}
                                className="rounded-full h-10 px-3 border-amber-300 text-amber-700 hover:bg-amber-50"
                                title="Scanner alle materialer og tilføjer varer hvor lager ≤ minimum"
                            >
                                <Zap className="w-4 h-4 mr-1.5" /> Auto-udfyld
                            </Button>
                        )}
                        <Button data-testid="shopping-add-btn" onClick={() => setAddOpen(true)} className="rounded-full h-10 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow">
                            <Plus className="w-4 h-4 mr-1.5" /> Tilføj vare
                        </Button>
                    </div>
                }
            />

            <SpecialNeedsSection />

            {totals && canSeeTotals && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5" data-testid="shopping-totals">
                    {[["I dag", totals.today], ["Denne uge", totals.week], ["Denne måned", totals.month]].map(([l, v]) => (
                        <div key={l} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between">
                            <div>
                                <div className="text-xs text-slate-500">{l}</div>
                                <div className="text-2xl font-bold text-slate-900">{fmtKr(v || 0)}</div>
                            </div>
                            <TrendingUp className="w-6 h-6 text-emerald-500" />
                        </div>
                    ))}
                </div>
            )}

            {loading && (
                <div className="mt-6 text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Indlæser…</div>
            )}
            {!loading && groups.length === 0 && (
                <div className="mt-6 text-center py-12 text-slate-500 bg-white border border-slate-200 rounded-xl">
                    <PackagePlus className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                    Ingen åbne varer at bestille — alt er allerede købt eller bestilt.
                </div>
            )}

            <div className="mt-5 space-y-4">
                {groups.map((g) => {
                    const isExp = expanded[g.supplier];
                    return (
                        <div key={g.supplier} data-testid={`order-group-${g.supplier}`} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                            {/* Group header */}
                            <div className="px-4 py-3 flex items-center gap-3 border-b border-slate-100 bg-slate-50/70">
                                <button onClick={() => setExpanded((e) => ({ ...e, [g.supplier]: !isExp }))} className="p-1 hover:bg-slate-200 rounded" data-testid={`order-toggle-${g.supplier}`}>
                                    {isExp ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                </button>
                                <Truck className="w-6 h-6 text-indigo-600" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-bold text-slate-900 text-lg">{g.supplier}</span>
                                        <span className="text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 font-semibold">{g.item_count} vare{g.item_count !== 1 ? "r" : ""}</span>
                                    </div>
                                    {canSeeTotals && (
                                        <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 mt-0.5">
                                            <span>Est. total: <strong className="text-slate-700">{fmtKr(g.estimated_total_ex_vat_dkk)}</strong> ex. moms</span>
                                            <span>· <strong className="text-slate-700">{fmtKr(g.estimated_total_incl_vat_dkk)}</strong> inkl. {Math.round((g.vat_rate || 0.25) * 100)} % moms</span>
                                            {g.items_missing_price_count > 0 && (
                                                <span className="text-amber-700 font-semibold flex items-center gap-0.5">
                                                    · <AlertTriangle className="w-3 h-3 inline" /> Ufuldstændigt prisestimat ({g.items_missing_price_count} vare{g.items_missing_price_count !== 1 ? "r" : ""} uden pris)
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-1.5 shrink-0">
                                    {g.supplier_url && (
                                        <a href={g.supplier_url} target="_blank" rel="noreferrer" data-testid={`order-open-${g.supplier}`}>
                                            <Button size="sm" variant="outline">
                                                <ExternalLink className="w-3.5 h-3.5 mr-1" /> Åbn hjemmeside
                                            </Button>
                                        </a>
                                    )}
                                    <Button size="sm" variant="outline" onClick={() => copyGroup(g.supplier, g.items)} data-testid={`order-copy-${g.supplier}`}>
                                        {copyToast[g.supplier] ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Kopieret</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Kopiér valgte</>}
                                    </Button>
                                    {canBatch && (
                                        <Button
                                            size="sm"
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                            disabled={groupSelected(g.items).length === 0}
                                            onClick={() => setOrderDialog({ supplier: g.supplier, item_ids: groupSelected(g.items), items: g.items.filter((i) => selected[i.id]) })}
                                            data-testid={`order-mark-${g.supplier}`}
                                        >
                                            <PackagePlus className="w-3.5 h-3.5 mr-1" /> Markér valgte som bestilt ({groupSelected(g.items).length})
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {/* Items */}
                            {isExp && (
                                <div className="divide-y divide-slate-100">
                                    <div className="px-4 py-2 bg-white flex items-center gap-2 text-[11px] font-semibold text-slate-500 uppercase">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4"
                                            checked={isAllSelected(g.items)}
                                            onChange={(e) => toggleAll(g.supplier, g.items, e.target.checked)}
                                            data-testid={`order-select-all-${g.supplier}`}
                                        />
                                        <span className="ml-1">Vælg alle · {groupSelected(g.items).length} valgt</span>
                                    </div>
                                    {g.items.map((it) => (
                                        <div key={it.id} data-testid={`order-item-${it.id}`} className="px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50">
                                            <input type="checkbox" className="w-4 h-4 shrink-0" checked={!!selected[it.id]} onChange={() => toggleItem(it.id)} data-testid={`order-check-${it.id}`} />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-semibold text-slate-900 truncate">{it.item_name}</span>
                                                    {it.material_number && <span className="text-[11px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">#{it.material_number}</span>}
                                                    <span className="text-sm text-slate-500">· {it.quantity_requested} {it.unit}</span>
                                                    {it.destination_type === "stock" ? (
                                                        <span className="text-[11px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700"><Warehouse className="w-3 h-3" /> Lager</span>
                                                    ) : (
                                                        <span className="text-[11px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700"><Briefcase className="w-3 h-3" /> {it.job_label || "Opgave"}</span>
                                                    )}
                                                </div>
                                                <div className="text-[11px] text-slate-500 flex gap-x-3 mt-0.5 flex-wrap">
                                                    {canSeeTotals && (it.has_price ? (
                                                        <span>Est. {fmtKr(it.estimated_line_total_ex_vat_dkk)} ex. moms ({fmtKr(it.buy_price_dkk)}/{it.unit})</span>
                                                    ) : (
                                                        <span className="text-amber-700">Ingen pris i materialekartotek</span>
                                                    ))}
                                                    {it.product_link && (
                                                        <a href={it.product_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-0.5" data-testid={`order-product-link-${it.id}`}>
                                                            <ExternalLink className="w-3 h-3" /> Se produkt
                                                        </a>
                                                    )}
                                                    {it.notes && <span className="text-slate-500 italic">· {it.notes}</span>}
                                                </div>
                                            </div>
                                            {isPrivileged && (
                                                <Button size="sm" variant="outline" onClick={() => setCancelItem(it)} data-testid={`order-cancel-${it.id}`} className="text-red-600 border-red-200 hover:bg-red-50 h-8 w-8 p-0">
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {orderDialog && (
                <MarkOrderedDialog
                    payload={orderDialog}
                    canSeePrice={canSeeTotals}
                    onClose={() => setOrderDialog(null)}
                    onDone={() => { setOrderDialog(null); setSelected({}); refresh(); }}
                />
            )}
            {addOpen && <AddItemDialog onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); refresh(); }} />}
            {cancelItem && <CancelItemDialog item={cancelItem} onClose={() => setCancelItem(null)} onDone={() => { setCancelItem(null); refresh(); }} />}
        </div>
    );
}

function MarkOrderedDialog({ payload, canSeePrice, onClose, onDone }) {
    const [orderRef, setOrderRef] = useState("");
    const [orderedAt, setOrderedAt] = useState(new Date().toISOString().slice(0, 10));
    const [expected, setExpected] = useState("");
    const [total, setTotal] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!orderRef.trim()) { toast.error("Ordre-/referencenummer er obligatorisk."); return; }
        setSaving(true);
        try {
            const body = {
                item_ids: payload.item_ids,
                order_reference: orderRef.trim(),
                ordered_at: orderedAt || undefined,
            };
            if (expected) body.expected_delivery_at = expected;
            if (canSeePrice && total !== "") body.actual_total_dkk = Number(total);
            if (note.trim()) body.note = note.trim();
            const r = await markGroupOrdered(body);
            toast.success(`${r.ordered_count} varer markeret som bestilt · ${r.order_reference}`);
            onDone();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><PackagePlus className="w-5 h-5 text-emerald-600" /> Markér som bestilt hos {payload.supplier}</DialogTitle></DialogHeader>
                <div className="mt-2 text-sm text-slate-600">{payload.item_ids.length} valgte varer</div>
                <div className="mt-3 space-y-3">
                    <div>
                        <Label>Ordre-/referencenummer *</Label>
                        <Input data-testid="order-ref" value={orderRef} onChange={(e) => setOrderRef(e.target.value)} placeholder="Fx SOLAR-2026-042" maxLength={120} className="mt-1 h-10" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Bestillingsdato</Label>
                            <Input data-testid="order-date" type="date" value={orderedAt} onChange={(e) => setOrderedAt(e.target.value)} className="mt-1 h-10" />
                        </div>
                        <div>
                            <Label>Forventet levering <span className="text-slate-400 text-xs">(valgfri)</span></Label>
                            <Input data-testid="order-expected" type="date" value={expected} onChange={(e) => setExpected(e.target.value)} className="mt-1 h-10" />
                        </div>
                    </div>
                    {canSeePrice && (
                        <div>
                            <Label>Faktisk totalpris ex. moms (kr) <span className="text-slate-400 text-xs">(valgfri — fordeles pro-rata)</span></Label>
                            <Input data-testid="order-total" type="number" min="0" step="0.01" value={total} onChange={(e) => setTotal(e.target.value)} className="mt-1 h-10" />
                        </div>
                    )}
                    <div>
                        <Label>Note <span className="text-slate-400 text-xs">(valgfri)</span></Label>
                        <textarea data-testid="order-note" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm" />
                    </div>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Annullér</Button>
                    <Button type="button" disabled={saving || !orderRef.trim()} onClick={submit} data-testid="order-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} Bekræft bestilling
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function AddItemDialog({ onClose, onCreated }) {
    const [jobs, setJobs] = useState([]);
    const [form, setForm] = useState({ item_name: "", quantity_requested: "", unit: "stk", destination_type: "stock", job_id: "", supplier: "", notes: "" });
    const [saving, setSaving] = useState(false);
    useEffect(() => { fetchJobs().then(setJobs).catch(() => setJobs([])); }, []);
    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
    const submit = async (ev) => {
        ev.preventDefault();
        if (!form.item_name.trim() || !form.quantity_requested) { toast.error("Udfyld varenavn og antal."); return; }
        if (form.destination_type === "job" && !form.job_id) { toast.error("Vælg et job."); return; }
        setSaving(true);
        try {
            const payload = { item_name: form.item_name.trim(), quantity_requested: Number(form.quantity_requested), unit: form.unit, destination_type: form.destination_type };
            if (form.destination_type === "job") payload.job_id = form.job_id;
            if (form.supplier.trim()) payload.supplier = form.supplier.trim();
            if (form.notes.trim()) payload.notes = form.notes.trim();
            await createShoppingItem(payload);
            toast.success("Vare tilføjet");
            onCreated();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };
    return (
        <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-blue-600" /> Tilføj indkøbsvare</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="mt-3 space-y-3" data-testid="shopping-add-form">
                    <div>
                        <Label>Varenavn *</Label>
                        <Input data-testid="shopping-item-name" required value={form.item_name} onChange={upd("item_name")} placeholder="Fx PN 3G 2.5 kabel" className="mt-1 h-10" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Antal *</Label>
                            <Input data-testid="shopping-qty" required type="number" min="0.01" step="0.01" value={form.quantity_requested} onChange={upd("quantity_requested")} className="mt-1 h-10" />
                        </div>
                        <div>
                            <Label>Enhed *</Label>
                            <select data-testid="shopping-unit" value={form.unit} onChange={upd("unit")} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-2 text-sm">
                                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <Label>Destination *</Label>
                        <div className="mt-1 grid grid-cols-2 gap-2">
                            {[{ v: "stock", l: "Lager", i: Warehouse }, { v: "job", l: "Bestemt opgave", i: Briefcase }].map((d) => (
                                <button key={d.v} type="button" data-testid={`shopping-dest-${d.v}`}
                                    onClick={() => setForm((f) => ({ ...f, destination_type: d.v, job_id: d.v === "stock" ? "" : f.job_id }))}
                                    className={`h-10 rounded-lg border px-3 flex items-center justify-center gap-1.5 text-sm font-medium ${form.destination_type === d.v ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-slate-200 text-slate-600"}`}
                                ><d.i className="w-4 h-4" /> {d.l}</button>
                            ))}
                        </div>
                    </div>
                    {form.destination_type === "job" && (
                        <div>
                            <Label>Job *</Label>
                            <select data-testid="shopping-job" required value={form.job_id} onChange={upd("job_id")} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-2 text-sm">
                                <option value="">— vælg job —</option>
                                {jobs.map((j) => <option key={j.id} value={j.id}>{j.job_number || j.id} · {j.title || ""} · {j.customer_name || ""}</option>)}
                            </select>
                        </div>
                    )}
                    <div>
                        <Label>Leverandør <span className="text-slate-400 text-xs">(valgfri)</span></Label>
                        <Input data-testid="shopping-supplier" value={form.supplier} onChange={upd("supplier")} placeholder="Bauhaus, Solar, Silvan…" className="mt-1 h-10" />
                    </div>
                    <div>
                        <Label>Note <span className="text-slate-400 text-xs">(valgfri)</span></Label>
                        <textarea data-testid="shopping-notes" rows={2} maxLength={2000} value={form.notes} onChange={upd("notes")} className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm" />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Annullér</Button>
                        <Button type="submit" disabled={saving} data-testid="shopping-add-submit" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                            {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} Tilføj
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function CancelItemDialog({ item, onClose, onDone }) {
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        if (reason.trim().length < 3) { toast.error("Årsag skal være mindst 3 tegn."); return; }
        setSaving(true);
        try { await cancelShoppingItem(item.id, reason.trim()); toast.success("Vare annulleret"); onDone(); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };
    return (
        <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-700"><AlertTriangle className="w-5 h-5" /> Annullér vare</DialogTitle></DialogHeader>
                <div className="mt-2 text-sm text-slate-600">{item.item_name}</div>
                <div className="mt-3">
                    <Label>Årsag *</Label>
                    <textarea data-testid="shopping-cancel-reason" rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm" placeholder="Fx: Fejloprettet — kunden ønsker ikke varen alligevel" />
                    <div className="mt-1 text-[11px] text-slate-500 flex justify-between"><span>Min. 3 · max 500</span><span>{reason.length}/500</span></div>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Annullér</Button>
                    <Button type="button" disabled={saving || reason.trim().length < 3} onClick={submit} data-testid="shopping-cancel-confirm" className="bg-red-600 hover:bg-red-700 text-white">
                        {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} Annullér vare
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
